import {
  Component,
  h,
  Event,
  EventEmitter,
  Prop,
  State,
  Watch,
  ComponentInterface,
} from "@stencil/core";
import { IThemeOptions } from "../../../models";
import {
  AppConfigService,
  ThemeService,
  TranslateService,
} from "../../../services";

@Component({
  tag: "av-pin",
  styleUrl: "av-pin.scss",
  shadow: true,
})
export class AvPin implements ComponentInterface {
  @State() themeOptions: IThemeOptions;

  @Prop() type: "number" | "text" = "text";
  @Prop() digits: number = 6;
  @Prop() ariaLabel: string;

  @Prop() value;

  @Watch("value")
  valueChange(value: string) {
    if (!value) {
      this.pinArray = new Array(this.digits);
      this.inputFieldsArray.forEach((input) => {
        input.value = null;
      });
    } else {
      if (value.length > this.digits || value === this.pinArray.join(""))
        return;
      this.pinArray = value.split("");
    }
  }

  @Event({
    eventName: "changed",
    composed: true,
    cancelable: true,
    bubbles: true,
  })
  changed: EventEmitter<string>;

  pinArray = new Array(this.digits);
  inputFieldsArray: HTMLInputElement[] = new Array(this.digits);
  // otpAbortCtrl: AbortController;

  constructor() {
    ThemeService.optionsAvailable().subscribe(
      (options) => (this.themeOptions = options)
    );
  }

  componentWillLoad(): void | Promise<void> {
    // doesn't seem to be working, Nice to have though
    // this.autoDetectOTP();
  }

  componentDidLoad(): void {
    setTimeout(() => this.inputFieldsArray?.[0]?.focus(), 100);
  }

  clearField(e: Event) {
    //@ts-ignore
    const input: HTMLInputElement = e.target;
    input.value = null;
    this.pinArray[Number(input.id)] = null;
    this.changed.emit(this.pinArray.join(""));
  }

  handleChange(e) {
    const input: HTMLInputElement = e.target;

    // handle paste
    if (input.id == "0" && input.value.length == this.digits) {
      this.pinArray = input.value.split("");
      input.blur();
      input.value = this.pinArray[0];
      for (let i = 0; i < this.digits; i++) {
        this.inputFieldsArray[i].value = this.pinArray[i];
      }
    } else {
      this.pinArray[Number(input.id)] = input.value;
      // @ts-ignore
      if (
        input.id != (this.digits - 1).toString() &&
        !(input.nextSibling as any).value
      ) {
        if (
          this.type === "text" &&
          Number(input.id) + 1 === 3 &&
          AppConfigService.ticketGroupsEnabled
        ) {
          // auto add "-"
          (input.nextSibling as HTMLInputElement).value = "-";
          (input.nextSibling.nextSibling as HTMLInputElement).focus();
          this.pinArray[Number((input.nextSibling as HTMLInputElement).id)] =
            "-";
        } else {
          (input.nextSibling as HTMLInputElement).focus();
        }
      } else {
        input.blur();
      }
    }

    // const allDigits =
    //   this.pinArray.filter((d) => d !== null).length === this.digits;
    // if (allDigits) {
    this.changed.emit(this.pinArray.join(""));
    // } else {
    // this.fieldChanged.emit();
    // }
  }

  ensureNumericInput(e): boolean {
    // Ensure that it is a number and stop the keypress
    if (
      (e.shiftKey || e.keyCode < 48 || e.keyCode > 57) &&
      (e.keyCode < 96 || e.keyCode > 105)
    ) {
      return false;
    }
    return true;
  }

  checkValue(e) {
    let valid = true;
    let isPaste = false;
    // todo: make it better
    if (
      [46, 9, 27, 13, 110, 190].indexOf(e.keyCode) !== -1 ||
      // Allow: Ctrl+A
      (e.keyCode === 65 && (e.ctrlKey || e.metaKey)) ||
      // Allow: Ctrl+C
      (e.keyCode === 67 && (e.ctrlKey || e.metaKey)) ||
      // Allow: Ctrl+V
      (e.keyCode === 86 && (e.ctrlKey || e.metaKey)) ||
      // Allow: Ctrl+X
      (e.keyCode === 88 && (e.ctrlKey || e.metaKey)) ||
      // Allow: home, end, left, right
      (e.keyCode >= 35 && e.keyCode <= 39)
    ) {
      valid = true;
      isPaste = true;
    } else if (e.keyCode === 8) {
      // Backspace
      // find the previous input (if any) and go there
      e.target.previousSibling?.focus();
      this.changed.emit(this.pinArray.join(""));
    }

    if (this.type === "number" && !isPaste) {
      valid = this.ensureNumericInput(e);
    }

    if (!valid) {
      e.preventDefault();
      return;
    }
  }

  registerInput(e) {
    this.inputFieldsArray[Number(e.id)] = e;
  }

  // autoDetectOTP() {
  //   if ("OTPCredential" in window) {
  //     try {
  //       this.otpAbortCtrl = new AbortController();
  //       CommonService.timer.setTimeout(() => {
  //         this.otpAbortCtrl.abort();
  //       }, 2 * 1000 * 60);

  //       navigator.credentials
  //         .get({
  //           // @ts-expect-error
  //           otp: { transport: ["sms"] },
  //           signal: this.otpAbortCtrl.signal,
  //         })
  //         .then((otp) => {
  //           Log.info(otp);
  //         })
  //         .catch((ex) => {
  //           Log.error(ex);
  //         });
  //     } catch (ex) {
  //       Log.error(ex);
  //     }
  //   }
  // }

  render() {
    const items = [];
    const digitText = TranslateService.instant("Digit");

    for (let i = 0; i < this.digits; i++) {
      items.push(
        <input
          type="text"
          autocapitalize="off"
          autocomplete="off"
          autocorrect="off"
          id={i.toString()}
          aria-label={`${digitText} ${i}`}
          style={{ "border-color": this.themeOptions.colorAccent }}
          ref={this.registerInput.bind(this)}
          onFocus={this.clearField.bind(this)}
          onInput={this.handleChange.bind(this)}
          onKeyDown={this.checkValue.bind(this)}
        />
      );
    }
    return (
      <div
        role="group"
        aria-label={this.ariaLabel}
        data-tid="pin/group"
        class={{ "av-pin": true, "av-pin-dark": this.themeOptions.darkMode }}
      >
        {items}
      </div>
    );
  }
}
